/* eslint-disable @typescript-eslint/camelcase */
import axios from 'axios';
import { Buffer } from 'buffer';
import ProviderUtils from '../ProviderUtils';
import { BASE_URL } from '../../constant';

import {
  Account,
  MetaData,
  PROVIDER_ALLIANCE,
  SwitchEthereumChainParams,
  TomoProviderEventName,
  initOptions,
} from '../../types/types';
import { messages, errorCodes, rpcErrors } from '../../message';
import { getSyncSiteMetadata } from '../../metaData';
import { vaildatorEIP712, resemblesEvmAddress } from '../../utils/validator';
import {
  loginDefineProperties,
  signDefineProperties,
} from '../../utils/properties';
import { AppInfo } from '../../constant';

import AbstractProvider from '../AbstractProvider';

function transfer16(val: string | number = 0) {
  val = isNaN(Number(val)) ? 1 : Number(val);
  return '0x' + val.toString(16);
}

const defaultAccount: Account = {
  address: '',
  chainId: '0x1',
  chainKey: 'ethereum',
  alliance: PROVIDER_ALLIANCE.EVM,
  chainName: 'Ethereum netWork',
  chainSymbol: 'ETH',
};

interface HttpProviderOptions {
  chainId: string;
  url: string;
}

interface RequestOptions {
  account: Account;
  metaData: initOptions['metaData'];
}

async function getChainList() {
  return await axios.get(
    'https://d13t1x9bdoguib.cloudfront.net/static/chainList.json'
  );
}

class HttpProvider {
  options: HttpProviderOptions;
  rpcMap: Map<number, string>;
  peddingMap: Map<number, any>;
  constructor(options?: HttpProviderOptions) {
    this.options = options;
    this.rpcMap = new Map();
    this.peddingMap = new Map();
    options?.chainId && this.setUrl(options.url, options.chainId);
  }
  setUrl(url, chainId) {
    this.rpcMap.set(parseInt(chainId), url);
  }
  getUrl(chainId) {
    return this.rpcMap.get(parseInt(chainId));
  }

  async send(
    payload: {
      method: string;
      params: any[];
      id?: number;
    },
    options: {
      chainId: string;
      chainKey?: string;
      rpcUrl?: string;
      account?: Account;
    }
  ) {
    let { method, params, id } = payload;
    const rpcUrl = options.rpcUrl || this.getUrl(options.chainId);
    // const { chainId } = options
    // const url = this.rpcMap.get(chainId)
    const requestParams = {
      jsonrpc: '2.0',
      method,
      params,
      id: id || new Date().getTime(),
    };

    const response = await axios.post(rpcUrl, requestParams);

    // return response.data
    const { result, error } = response.data;
    if (error) {
      throw error;
    }
    return response.data.result;
  }

  async sendBatch(payloads, options = {}) {
    const results = [];
    for (const payload of payloads) {
      const result = await this.send(payload, options as any);
      results.push(result);
    }
    return results;
  }
}

class ProxyLocalStorage {
  prefix = 'tomo-tg-wallet-';
  constructor() {}
  get(key) {
    try {
      let value = localStorage.getItem(`${this.prefix}${key}`);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }
  set(key, value) {
    try {
      localStorage.setItem(`${this.prefix}${key}`, JSON.stringify(value));
      return value;
    } catch (error) {
      return null;
    }
  }
}

function ProxyResponse(this: EthereumProvider, responsePayload) {
  const { method, config, params, result } = responsePayload || {};
  const requestParams = config?.params?.[0] || {};
  if (config) {
    const accounts = config?.accounts;
    accounts && (this._accounts = accounts);

    switch (method) {
      case 'wallet_switchEthereumChain':
      case 'wallet_addEthereumChain':
        const isExist = Object.values(this._accounts || {}).find(
          account =>
            transfer16(account.chainId) === transfer16(requestParams.chainId)
        );
        if (isExist) {
          this._account = {
            ...isExist,
          };
        } else {
          responsePayload.error = {
            code: -32602,
            message: `tomo wallet does not support  ${requestParams?.chainId}`,
          };
        }
        break;
      case 'eth_requestAccounts':
        this._account =
          this._accounts[this._account?.chainKey] ||
          Object.values(this._accounts)[0];
        break;
      default:
        break;
    }
    return responsePayload;
  }
}

const walletPromiseTimeoutClear = (() => {
  const walletPromiseMap: Map<string, number> = new Map();

  return (id, fn, timeout = 1000) => {
    if (walletPromiseMap.has(id)) return walletPromiseMap.has(id);
    const promise = fn();
    walletPromiseMap.set(id, promise);
    setTimeout(() => {
      walletPromiseMap.delete(id);
    }, timeout);
    return promise.finally(() => {
      walletPromiseMap.delete(id);
    });
  };
})();

const isReConnect = (lastTime, timeout = 1000 * 60 * 15) => {
  const now = new Date().getTime();
  return now - lastTime > timeout;
};

interface OverrideRpcUrl {
  [chainId: number]: string;
}

export class EthereumProvider extends AbstractProvider {
  version: string;
  // state: any
  connectUrl: string;
  bridgeUrl: string;
  connect_direct_link: string;
  httpProvider: HttpProvider;
  storage: ProxyLocalStorage;
  utils: ProviderUtils;
  eventTimeout: number;
  metaData: MetaData;

  public autoRefreshOnNetworkChange = true;
  public _isMetaMask = true;
  public isMetaMask = true;
  public _isConnected = false;
  public isTomoWallet = true;
  private _isRetryCount = 0;
  private chainList: { id: number; name: string; rpcUrl }[] = [];
  private overrideRpcUrl: OverrideRpcUrl = {};

  // _accounts: {
  //     [key: string]: Account
  // }
  // _account: Account | null
  get _lastTime() {
    return isNaN(Number(this.storage.get('lastTime') || 0))
      ? 0
      : Number(this.storage.get('lastTime') || 0);
  }
  set _lastTime(value) {
    this.storage.set('lastTime', value);
  }
  get _account() {
    return this.storage.get('evm_account') || defaultAccount;
  }
  set _account(account: Account | null) {
    const oldAccount = { ...(this._account || defaultAccount) };

    if (!account) {
      account = {
        ...oldAccount,
        address: '',
      };
    }
    this.storage.set('evm_account', account);

    if (transfer16(oldAccount?.chainId) != transfer16(account?.chainId)) {
      this.emit('chainChanged', this?.chainId, account?.alliance, account);
      this.emit('networkChanged', parseInt(this?.chainId), account?.alliance);
    }
    if (oldAccount?.address != account?.address) {
      this.emit('accountsChanged', account?.address ? [account?.address] : []);
    }
  }
  get _accounts() {
    return (
      this.storage.get('evm_accounts') || {
        ethereum: defaultAccount,
      }
    );
  }
  set _accounts(accounts: { [key: string]: Account }) {
    this.storage.set('evm_accounts', accounts);
  }

  get networkVersion() {
    return parseInt(this.chainId);
  }

  get chainId() {
    const chainId = this.utils.storage.get('evm_chainId');
    return transfer16(chainId);
  }

  get chainKey() {
    return this._account?.chainKey || defaultAccount?.chainKey;
  }

  get connected() {
    return this._account?.address ? true : false;
  }
  get selectedAddress() {
    return this._account?.address || '';
  }

  constructor(options: initOptions & { overrideRpcUrl?: OverrideRpcUrl }) {
    super({
      protocol: PROVIDER_ALLIANCE.EVM,
    });

    if (options.overrideRpcUrl) this.overrideRpcUrl = options.overrideRpcUrl;

    this.utils = new ProviderUtils({
      ...BASE_URL,
      ...options,
    });

    this.version = this.getAppInfo().version;

    this.connectUrl = options.connect || BASE_URL.connect;
    this.bridgeUrl = options.bridge || BASE_URL.bridge;
    this.connect_direct_link =
      options.connect_direct_link || BASE_URL.connect_direct_link;

    this.eventTimeout = options?.eventTimeout || 10 * 60 * 1000;
    this.metaData = options.metaData;

    this.storage = new ProxyLocalStorage();
    this.httpProvider = new HttpProvider({
      chainId: '0x1',
      url: 'https://rpc.ankr.com/eth',
    });

    this._initialize();
    getChainList()
      .then(response => (this.chainList = response.data as any))
      .catch(() => {
        // todo what if cannot fetch chain list
      });
    if (
      typeof window !== 'undefined' &&
      !window?.ethereum &&
      options.injected
    ) {
      window.ethereum = this;
      console.log('injected in evm provider');
      dispatchEvent(new Event('ethereum#initialized'));
    }
  }

  checkIsChainSupported(chainId: string | number) {
    const chainIdNum = Number(chainId);
    return !!this.chainList.find(c => c.id === chainIdNum);
  }

  _initialize() {
    this.emit('connect', { chainId: this?.chainId });
    this.emit('_initialized');
  }

  async _walletSwitchChain(payload: {
    method: string;
    params: Array<SwitchEthereumChainParams>;
  }) {
    const { method, params } = payload;
    const requestParams = params[0];
    let isExist = Object.values(this._accounts || {}).find(
      account =>
        transfer16(account.chainId) === transfer16(requestParams.chainId)
    );
    if (!isExist) {
      await this.request({
        method: 'eth_requestAccounts',
      });
      isExist = Object.values(this._accounts || {}).find(
        account =>
          transfer16(account.chainId) === transfer16(requestParams.chainId)
      );
      if (!isExist) {
        throw rpcErrors.invalidRequest({
          code: errorCodes.provider.unsupportedChain,
          message: messages.errors.invalidChains(
            requestParams?.chainId.toString()
          ),
          data: params,
        });
      }
    }
    this._account = isExist;
    return null;
  }

  getAppInfo() {
    return {
      ...AppInfo,
    };
  }

  async enable() {
    return this.request({ method: 'eth_requestAccounts' });
  }

  get isConnected() {
    return this.connected;
  }

  getRpcUrl() {
    let rpcUrl = this.chainList.find(c => c.id === Number(this.chainId)).rpcUrl;
    if (this.overrideRpcUrl[+this.chainId])
      rpcUrl = this.overrideRpcUrl[+this.chainId];
    return rpcUrl;
  }

  async request(payload: { method: string; params?: any[] }): Promise<any> {
    const { method, params = [] } = payload || {};

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw rpcErrors.invalidRequest({
        message: messages.errors.invalidRequestArgs(),
        data: params,
      });
    }

    if (typeof method !== 'string' || method.length === 0) {
      throw rpcErrors.invalidRequest({
        message: messages.errors.invalidRequestMethod(),
        data: payload as any,
      });
    }

    try {
      const requestKey = `${method}-${JSON.stringify(params || [])}`;

      switch (method) {
        case 'wallet_requestPermissions': // this is for rainbowkit
          throw 'wallet_requestPermissions not supported';
        case 'eth_requestAccounts':
          const isRreConnected = isReConnect(this._lastTime, this.eventTimeout);
          if (this?._account?.address && !isRreConnected) {
            return [this._account.address];
          }
          return walletPromiseTimeoutClear(requestKey, () =>
            this._request(method, params)
          );
        case 'eth_accounts':
          return [this._account?.address];
        case 'eth_chainId':
          return this.chainId;
        case 'wallet_switchEthereumChain':
        case 'wallet_addEthereumChain': {
          if (!this.connected) {
            await this.request({
              method: 'eth_requestAccounts',
            });
          }
          const { chainId } = params[0] as any;
          // const isChainSupported = this.checkIsChainSupported(chainId);
          this.utils.storage.set('evm_chainId', chainId);
          // if (!isChainSupported)
          //  `chain ${chainId} is not supported`;
          return null;
        }
        case 'wallet_watchAsset':
        // case 'wallet_addSeed': // todo seems to be implemented already
        case 'metamask_watchAsset':
          return walletPromiseTimeoutClear(requestKey, () =>
            this._request(method, params)
          );
        case 'personal_sign':
        case 'eth_signETHTransaction':
        case 'eth_signErc20Transaction':
        case 'eth_signTypedData':
        case 'eth_signTransaction':
          if (!this.connected) {
            await this.request({
              method: 'eth_requestAccounts',
            });
          }
          return walletPromiseTimeoutClear(requestKey, () =>
            this._request(method, params)
          );
        case 'eth_sendTransaction': {
          const param = params[0];
          let { chainId: chainIdResolved } = param as any;
          if (!chainIdResolved) chainIdResolved = this.chainId;
          const signRes = await this.request({
            method: 'eth_signTransaction',
            params: [
              {
                ...param,
                chainId: chainIdResolved,
              },
            ],
          });

          const rpc = this.getRpcUrl();
          const signedTx = signRes.result;
          return this.httpProvider.send(
            {
              method: 'eth_sendRawTransaction',
              params: [signedTx],
            },
            {
              chainId: chainIdResolved,
              chainKey: this.chainKey,
              rpcUrl: rpc, // TODO
            }
          );
        }
        case 'eth_signTypedData_v3':
        case 'eth_signTypedData_v4':
          if (!this.connected) {
            await this.request({
              method: 'eth_requestAccounts',
            });
          }
          let signPersonalMessageV4 = payload.params[0];
          if (
            resemblesEvmAddress(payload.params[0]) &&
            !resemblesEvmAddress(payload.params[1])
          ) {
            signPersonalMessageV4 = payload.params[1];
          }
          params[0] = vaildatorEIP712(signPersonalMessageV4);
          return walletPromiseTimeoutClear(requestKey, () =>
            this._request(method, params)
          );
        default: {
          const rpcUrl = this.getRpcUrl();
          return this.httpProvider.send(payload as any, {
            chainId: this.chainId,
            chainKey: this.chainKey,
            rpcUrl, // TODO
          });
        }
      }
    } catch (error) {
      console.log(error);
      return Promise.reject(
        typeof error == 'object'
          ? error
          : {
              code: -32603,
              message: error,
            }
      );
    }
  }

  async _createEventSource(
    hash,
    signature,
    salt: string,
    direct_params: { method: string; params: any[] },
    request
  ) {
    const eventSource = new EventSource(
      `${this.bridgeUrl}?appid=${hash}&signature=${signature}&salt=${salt}`
    );
    return new Promise((resolve, reject) => {
      const timer =
        this.eventTimeout > 0
          ? setTimeout(() => {
              reject(
                rpcErrors.invalidRequest({
                  code: errorCodes?.rpc?.timeoutRequest,
                  message: messages.errors.timeOut(direct_params.method),
                  data: direct_params.params,
                })
              );
              eventSource.close();
            }, this.eventTimeout || 60000)
          : null;
      // @ts-ignore
      eventSource.addEventListener('message', event => {
        // @ts-ignore
        if (event?.data === 'hi' || event?.data === 'close') return;
        if (!event?.data) {
          this._isRetryCount += 1;
          eventSource.close();
          clearTimeout(timer);
          if (this._isRetryCount > 4) {
            this._isRetryCount = 0;
            throw new Error('Tomo Wallet Request error');
          }
          return this._createEventSource(
            hash,
            signature,
            salt,
            direct_params,
            request
          );
        }
        try {
          const decodedString = atob(event?.data);
          let data = JSON.parse(decodedString);
          if (
            request.method === 'eth_requestAccounts' ||
            request.method === 'wallet_requestPermissions'
          ) {
            data = loginDefineProperties(request, data);
          } else if (
            request.method === 'eth_signETHTransaction' ||
            request.method === 'eth_signTransaction'
          ) {
            const source = this._account;
            data = signDefineProperties(request, data, source);
          } else if (request.method === 'personal_sign') {
            const source = this._account;
            console.log('personal_sign data', data);
            data = signDefineProperties(request, data.result, source);
          } else if (request.method === 'eth_signErc20Transaction') {
            const source = this._account;
            data = signDefineProperties(request, data, source);
          }
          // not supposes to support
          // else if (request.method === 'wallet_addSeed') {
          // const source = this._account;
          // data = signDefineProperties(request, data, source);
          // const acc_ = this._accounts
          // if (data.result.code === 10000) {
          //   acc_.eth.account = data.result.result.wallet_address;
          //   acc_.eth.address = data.result.result.wallet_address;
          // this._accounts = acc_;
          // this._account =  acc_.eth;
          // }
          // }
          if (data?.id == request.id || salt == data?.salt) {
            eventSource?.close();

            clearTimeout(timer);
            if (data.reConnect || !data.error) {
              ProxyResponse.call(this, data);
            }
            this._isRetryCount = 0;
            data.error ? reject(data.error) : resolve(data.result);
          } else {
            console.log('Tomo wallet data error');
          }
        } catch (error) {
          console.log(error);
        }
      });

      this.utils.openAuthBot(direct_params);
    });
  }

  async _request(method, params, options?: RequestOptions) {
    const account = this._account;
    const now = new Date().getTime();
    this._lastTime = now;
    options = {
      account: account,
      metaData: getSyncSiteMetadata(this.metaData),
      ...options,
      timeStamp: now,
    } as any;
    const id =
      new Date().getTime() + Math.floor(Math.random() * 1000).toString();

    let salt = `salt-${Date.now()}-${id}`;
    salt = Buffer.from(salt, 'utf-8')
      .toString('base64')
      .replace('==', '')
      .replace('=', '');

    const payload = {
      id,
      method,
      params,
      options,
    };

    // push参数
    const publish_params = {
      id: payload.id,
      data: payload,
      version: '1.0',
      salt: salt,
    };
    const response = await axios.post(
      this.connectUrl,
      {
        payload_base64: Buffer.from(
          JSON.stringify(publish_params),
          'utf-8'
        ).toString('base64'),
      },
      {
        headers: {
          'X-Salt': salt,
        },
      }
    );

    const { hash, signature } = response.data;
    //深度链参数
    const direct_params = {
      method,
      params: [salt, hash, signature],
    };
    const request = {
      ...payload,
      hash,
      signature,
      salt,
    };
    return this._createEventSource(
      hash,
      signature,
      salt,
      direct_params,
      request
    );
  }

  disconnect() {
    this._account = null;

    this.emit('accountsChanged', []);
    this.emit(TomoProviderEventName.TOMO_EVM_PROVIDER_DISCONNECTED, '');
    // this.emit('close', "");
  }
}
